import { ShareBaseNotification } from 'phoenix/domain/sharebase-notification';
import {
	ClientNotification,
	NotificationTaskAction,
	TaskAction
} from 'sharebase/domain';
import { NotificationFactory } from 'sharebase/factories/notificationFactory';
import { ActionService } from 'phoenix/services';

/**
* @ngdoc service
* @name  sharebase-app.sbaNotificationDataStore
* 
* @description 
* A datastore used to interact with user notifications
* 
**/
export class NotificationDataStore {
	public static $inject = [
		'pxActionService',
		'sbNotificationsService',
		'sbNotificationFactory'
	];

	private _notifications:ClientNotification[] = [];
	public badgeCount:number;
	/**
	 * The user's notifications.
	 */
	public get notifications() {
		return this._notifications;
	}

	public get unread() {
		return this.notifications.filter(n => n.Unread);
	}

	constructor(
		private readonly pxActionService:ActionService,
		private readonly sbNotificationsService,
		private readonly sbNotificationFactory:NotificationFactory
	) {}
	
	/**
	* @ngdoc method
	* @name sbaNotificationDataStore#load
	* @methodOf sharebase-app.sbaNotificationDataStore
	* @kind function
	*
	* @description
	* Loads or refreshes the user's notifications
	*
	* @param {max} max Indicates that the API needs to return
	* the {max} latest notifications.
	*
	* @returns {Deferred} A promise that will resolve with
	* an array of notifications
	*/
	public load(max:number): ng.IPromise<ClientNotification[]> { 
		this._notifications = [];
		// order is important here, since getting all notifications marks them as read
		return this
			.loadUnread()
			.then(() => this.sbNotificationsService.getNotifications(max))		
			.then((notifications:ShareBaseNotification[]) => {
				let allNotifications = notifications.map(n => this.sbNotificationFactory.create(n));
				allNotifications.forEach(n => {
					if (!this.contains(this._notifications, n)){
						this._notifications.push(n);
					}
				});
				return this.notifications;
			});
	}

	/**
	* @ngdoc method
	* @name sbaNotificationDataStore#loadUnread
	* @methodOf sharebase-app.sbaNotificationDataStore
	* @kind function
	*
	* @description
	* Loads or refreshes unread notifications
	*
	* @returns {Deferred} A promise that will resolve with
	* an array of unread notifications
	*/
	public loadUnread(): ng.IPromise<ClientNotification[]> {
		return this.sbNotificationsService
			.getUnreadNotifications()
			.then((unread:ShareBaseNotification[]) => {
				let taskIds:number[] = [];
				let _unread = unread.map(n => this.sbNotificationFactory.create(n));
				_unread.forEach(n => {
					n.Unread = true;
					if (!this.contains(this._notifications, n)){
						this._notifications.push(n);

						if(n.isTaskNotification){
							let taskId = n.tokens.filter(t => t.tuple && t.tuple.key === 'Task')[0].tuple.value;
							taskIds.push(taskId);
						}
					}
				});

				if(taskIds.length){
					let uniqueTaskIds = taskIds.filter((taskId, index, uniqueIds) => uniqueIds.indexOf(taskId) === index);
					this.pxActionService.emit({
						type: TaskAction.TaskNotificationReceived,
						payload: uniqueTaskIds
					});
				}

				return _unread;
			});
	}
	
	/**
	 * 
	 * @param checkArray Array to be looped through to compare its notifications to {notification}
	 * @param notification Notification to be checked for uniqueness within {checkArray}
	 */
	private contains(checkArray: ClientNotification[], notification: ClientNotification): boolean {
		return checkArray.some(n => n.id === notification.id);
	}

	/**
	* @ngdoc method
	* @name sbaNotificationDataStore#clearAll
	* @methodOf sharebase-app.sbaNotificationDataStore
	* @kind function
	*
	* @description
	* Clears all notifications
	*
	* @returns {Deferred} A promise that will resolve success
	*/
	public clearAll(){
		return this.sbNotificationsService.deleteAllNotifications()
			.then(response => {
				this._notifications = [];
			});
	}

	/**
	* @ngdoc method
	* @name sbaNotificationDataStore#clearNotification
	* @methodOf sharebase-app.sbaNotificationDataStore
	* @kind function
	*
	* @description
	* Clears a specific notification
	*
	* @param {Object} notification the notification representation to clear
	* @param {Bool} updateDS Optional - wheither to auto-remove cleard item from DS, default is true		
	* @returns {Deferred} A promise that will resolve success
	*/
	public clearNotification(notification:ClientNotification, updateDS:boolean = true){		
		return this.sbNotificationsService
			.deleteNotification(notification.toApi())
			.then(() => {
				notification.Unread = false;
				if(updateDS){
					//remove from ds
					let index = this._notifications.indexOf(notification);
					this._notifications.splice(index, 1);
				}
			});
	}
}


// WEBPACK FOOTER //
// ./src/sharebase-app/dataStores/notificationDataStore.ts